import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "../Context/AppContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import OtpInput from "./auth/OtpInput";
import { useTranslation } from "react-i18next";
import PasswordRequirementFeedback from "./auth/PasswordRequirementFeedback";
import {
  getPasswordRequirementFeedback,
  isStrongPassword,
  PASSWORD_REQUIREMENT_ERROR_MESSAGE,
} from "../auth/passwordPolicy";
import {
  login as loginApi,
  register as registerApi,
  socialLogin as socialLoginApi,
  verifyEmail as verifyEmailApi,
  resendVerificationEmail as resendVerificationEmailApi,
  forgotPassword as forgotPasswordApi,
  resetPassword as resetPasswordApi,
} from "../auth/authApi";
import { setTokens } from "../auth/authStorage";
import { useDebouncedEmailExistsCheck } from "../auth/useDebouncedEmailExistsCheck";
import {
  clearAuthSensitiveQueries,
  isUserDoesNotExistError,
} from "../auth/authSession";
import { trySyncExplorerPublicProfileBeforeSignup } from "../explorer/explorerPreLoginSignupSync";
import {
  ExplorerProfileSyncError,
  resolveExplorerGuardedPath,
  syncExplorerStateAfterAuth,
} from "../explorer/explorerProfileSync";
import {
  getExplorerPostMembershipEventCombo,
  getExplorerEventId,
  getExplorerLocalProfile,
  getExplorerPendingOnboarding,
} from "../explorer/explorerSession";
import axiosInstance, { getAPIURL } from "../api/axiosInstance";
import { getEarlyStageStatus } from "../api/earlyStageMembershipApi";
import { isEarlyStageRejectionNoticeVisible } from "../utils/earlyStageRejectionNotice";
import { shouldResumeEarlyStageRegistration } from "../utils/earlyStageRegistrationGate";
import LiveTranslationDropdown from "./common/LiveTranslationDropdown";
import "./css/AuthFlow.css";

type RegisterPhase = "email" | "profile" | "verify";

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

function safeInternalPath(redirect: string | null): string {
  if (!redirect) return "";
  const t = redirect.trim();
  if (!t.startsWith("/") || t.startsWith("//")) return "";
  return t;
}

function getApiErrorCode(err: any): string {
  const raw = err?.response?.data?.code;
  return typeof raw === "string" ? raw.trim().toLowerCase() : "";
}

function Login() {
  const { t, i18n } = useTranslation();
  const uiLang = i18n.resolvedLanguage || i18n.language;
  const queryClient = useQueryClient();
  const { setObjectId, setUser } = useAppContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectAfterAuth = safeInternalPath(searchParams.get("redirect"));
  const isExplorerAuthUi =
    searchParams.get("explorer") === "1" || searchParams.get("flow") === "explorer";
  const [mode, setMode] = useState<"login" | "register">("login");
  const [registerPhase, setRegisterPhase] = useState<RegisterPhase>("email");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** True when register returned 409 (email already exists) and we moved to the verify step. */
  const [verifyStepFromDuplicateEmail, setVerifyStepFromDuplicateEmail] = useState(false);
  /** True when login returned email_not_verified and we moved to the verify step. */
  const [verifyStepFromLogin, setVerifyStepFromLogin] = useState(false);
  const socialCallbackHandledRef = useRef(false);

  const isRegisterEmailStep = mode === "register" && registerPhase === "email";
  const { exists: emailExists, needsEmailVerification, checking: emailExistsChecking, checkNow: checkEmailNow } =
    useDebouncedEmailExistsCheck(email, isRegisterEmailStep);

  const getLocalizedError = useCallback(
    (err: any, fallbackKey: string, codePrefix = "auth.errorCode"): string => {
      const code = getApiErrorCode(err);
      if (code) {
        const codeKey = `${codePrefix}.${code}`;
        if (i18n.exists(codeKey)) return t(codeKey);
      }
      return t(fallbackKey);
    },
    [i18n, t]
  );

  useEffect(() => {
    const modeParam = searchParams.get("mode");
    const emailParam = searchParams.get("email");
    const fn = searchParams.get("prefillFirstName");
    const ln = searchParams.get("prefillLastName");

    if (emailParam && isValidEmail(emailParam)) {
      setEmail(emailParam);
    }
    if (fn) setFirstName(fn);
    if (ln) setLastName(ln);

    if (modeParam === "register") {
      setMode("register");
      if (emailParam && isValidEmail(emailParam)) {
        setRegisterPhase("profile");
      } else {
        setRegisterPhase("email");
      }
    }
  }, [searchParams]);

  const resetRegisterFlow = useCallback(() => {
    setRegisterPhase("email");
    setVerificationCode("");
    setPassword("");
    setFirstName("");
    setLastName("");
    setError(null);
    setInfo(null);
    setVerifyStepFromDuplicateEmail(false);
    setVerifyStepFromLogin(false);
  }, []);

  const switchToLogin = useCallback(() => {
    setMode("login");
    resetRegisterFlow();
    setShowForgotPassword(false);
    setError(null);
    setInfo(null);
  }, [resetRegisterFlow]);

  const switchToRegister = useCallback(() => {
    setMode("register");
    setRegisterPhase("email");
    setShowForgotPassword(false);
    setError(null);
    setInfo(null);
    setPassword("");
    setFirstName("");
    setLastName("");
    setVerificationCode("");
    setVerifyStepFromDuplicateEmail(false);
    setVerifyStepFromLogin(false);
  }, []);

  const canLoginSubmit = useMemo(
    () => email.trim().length > 0 && password.trim().length > 0,
    [email, password]
  );

  const canRegisterEmailNext = useMemo(() => isValidEmail(email), [email]);

  const canRegisterProfileSubmit = useMemo(
    () =>
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      isStrongPassword(password.trim()) &&
      isValidEmail(email),
    [email, firstName, lastName, password]
  );
  const registerPasswordFeedback = useMemo(() => getPasswordRequirementFeedback(password), [password]);

  const resetPasswordFeedback = useMemo(() => getPasswordRequirementFeedback(newPassword), [newPassword]);

  /**
   * Post-login / verify destination. OAuth/social return strips `?redirect=` / `explorer=1` from the URL,
   * so membership+event combo must rely on session (see ExplorerCheckout goMembershipFirst).
   * Normal portal login (no Explorer flags, no pending combo) → `redirect` or /dashboard only.
   */
  const resolvePostAuthPath = useCallback(async (objectId?: string) => {
    const pendingMembershipEventCombo = !!getExplorerPostMembershipEventCombo();
    if (pendingMembershipEventCombo) {
      return "/register-user";
    }
    const explorerEventId = getExplorerEventId();
    const isExplorerContext = isExplorerAuthUi || !!explorerEventId;
    if (objectId?.trim()) {
      try {
        const { data } = await axiosInstance.get(
          getAPIURL(`/User/ByObjectId/${encodeURIComponent(objectId.trim())}`),
        );
        const user = data?.user ?? data?.User;
        if (shouldResumeEarlyStageRegistration(null, user)) {
          return "/register-user";
        }
        try {
          const esStatus = await getEarlyStageStatus();
          const userObjectId =
            (typeof user?.objectId === "string" && user.objectId.trim()) ||
            (typeof user?.ObjectId === "string" && user.ObjectId.trim()) ||
            objectId.trim();
          if (isEarlyStageRejectionNoticeVisible(esStatus, userObjectId)) {
            return "/register-user";
          }
        } catch {
          /* not early stage or status unavailable */
        }
        if (!isExplorerContext && user?.hasPaid !== 1) {
          return "/register-user";
        }
      } catch (err) {
        if (!isExplorerContext && isUserDoesNotExistError(err)) {
          return "/register-user";
        }
      }
    } else if (!isExplorerContext) {
      return "/register-user";
    }
    let destination = redirectAfterAuth || "/dashboard";
    if (isExplorerAuthUi || explorerEventId) {
      if (explorerEventId && redirectAfterAuth === "/event-booking") {
        destination = `/confirm-booking/${explorerEventId}`;
      } else if (explorerEventId && !redirectAfterAuth) {
        destination = `/confirm-booking/${explorerEventId}`;
      }
      destination = await resolveExplorerGuardedPath(objectId, destination, {
        explorerContext: true,
      });
    }
    return destination;
  }, [isExplorerAuthUi, redirectAfterAuth]);

  /** Pushes explorer profile + pending step-2 data after successful sign-in. */
  const syncExplorerOnOtpVerify = useCallback(async () => {
    const pending = getExplorerPendingOnboarding();
    const local = getExplorerLocalProfile();
    const explorerEventId = getExplorerEventId();
    if (!isExplorerAuthUi && !pending?.segment && !local && !explorerEventId) return;
    await syncExplorerStateAfterAuth();
  }, [isExplorerAuthUi]);

  const applyAuthSession = useCallback(
    (result: {
      accessToken: string;
      refreshToken: string;
      expiresAtUtc?: string;
      userObjectId: string;
      email?: string;
    }) => {
      clearAuthSensitiveQueries(queryClient);
      setUser(undefined);
      setTokens({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAtUtc: result.expiresAtUtc,
        userObjectId: result.userObjectId,
        email: result.email,
      });
      setObjectId(result.userObjectId);
    },
    [queryClient, setObjectId, setUser],
  );

  const handleExplorerSyncFailure = useCallback(
    (err: unknown): boolean => {
      if (err instanceof ExplorerProfileSyncError) {
        setError(
          err.message ||
            t("explorer.segmentation.saveFailed", {
              defaultValue: "Could not save your explorer profile. Please complete signup.",
            }),
        );
        navigate("/explorer/signup", { replace: true });
        return true;
      }
      const code = (err as { response?: { data?: { code?: string } } })?.response
        ?.data?.code;
      if (code === "profile_required" || code === "email_mismatch" || code === "profile_incomplete") {
        setError(
          t("explorer.segmentation.profileMissing", {
            defaultValue: "Please complete your profile before continuing.",
          }),
        );
        navigate("/explorer/signup", { replace: true });
        return true;
      }
      return false;
    },
    [navigate, t],
  );

  const onLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      const result = await loginApi(email.trim(), password.trim());
      applyAuthSession(result);
      try {
        await syncExplorerOnOtpVerify();
      } catch (syncErr) {
        if (handleExplorerSyncFailure(syncErr)) return;
        throw syncErr;
      }
      navigate(await resolvePostAuthPath(result.userObjectId), { replace: true });
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === "email_not_verified") {
        setError(null);
        setMode("register");
        setRegisterPhase("verify");
        setVerificationCode("");
        setVerifyStepFromDuplicateEmail(false);
        setVerifyStepFromLogin(true);
        setInfo(t("auth.emailAlreadyExistsVerify"));
      } else if (code === "password_not_set") {
        setError(null);
        setInfo(t("auth.useForgotPasswordHint"));
        setShowForgotPassword(true);
      } else {
        setError(getLocalizedError(err, "auth.loginFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const onRegisterFromProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!isStrongPassword(password.trim())) {
      setError(PASSWORD_REQUIREMENT_ERROR_MESSAGE);
      return;
    }
    setLoading(true);
    try {
      if (isExplorerAuthUi) {
        await trySyncExplorerPublicProfileBeforeSignup({
          emailForSignup: email.trim(),
        });
      }
      const start = await registerApi({
        email: email.trim(),
        password: password.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      try {
        const raw = localStorage.getItem("UserData");
        const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
        localStorage.setItem(
          "UserData",
          JSON.stringify({
            ...parsed,
            email: email.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
          }),
        );
      } catch {
        /* ignore corrupt storage */
      }
      if (start.requiresEmailVerification) {
        setRegisterPhase("verify");
        setVerificationCode("");
        setVerifyStepFromDuplicateEmail(false);
        setVerifyStepFromLogin(false);
      }
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setRegisterPhase("verify");
        setVerificationCode("");
        setVerifyStepFromDuplicateEmail(true);
        setVerifyStepFromLogin(false);
        setInfo(t("auth.emailAlreadyExistsVerify"));
        setError(null);
      } else {
        setError(getLocalizedError(err, "auth.registrationFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const onVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const code = verificationCode.replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      setError(t("auth.enterSixDigitCode"));
      return;
    }
    setLoading(true);
    try {
      const result = await verifyEmailApi({ email: email.trim(), code });
      applyAuthSession(result);
      try {
        await syncExplorerOnOtpVerify();
      } catch (syncErr) {
        if (handleExplorerSyncFailure(syncErr)) return;
        throw syncErr;
      }
      navigate(await resolvePostAuthPath(result.userObjectId), { replace: true });
    } catch (err: any) {
      setError(getLocalizedError(err, "auth.verificationFailed"));
    } finally {
      setLoading(false);
    }
  };

  const onResendVerificationEmail = async () => {
    if (!isValidEmail(email)) {
      setError(t("auth.enterValidEmail"));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await resendVerificationEmailApi(email.trim());
      setInfo(t("auth.verificationCodeResent"));
    } catch (err: any) {
      setError(getLocalizedError(err, "auth.resendCodeFailed"));
    } finally {
      setLoading(false);
    }
  };

  const onForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      await forgotPasswordApi(email.trim());
      setInfo(t("auth.resetCodeSent"));
      setResetCodeSent(true);
    } catch (err: any) {
      setError(getLocalizedError(err, "auth.requestFailed"));
    } finally {
      setLoading(false);
    }
  };

  const onResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!isStrongPassword(newPassword.trim())) {
      setError(PASSWORD_REQUIREMENT_ERROR_MESSAGE);
      return;
    }
    setLoading(true);
    try {
      await resetPasswordApi({
        email: email.trim(),
        code: resetCode.trim(),
        newPassword,
      });
      setInfo(t("auth.passwordResetSuccess"));
      setShowForgotPassword(false);
      setResetCode("");
      setNewPassword("");
      setShowNewPassword(false);
      setResetCodeSent(false);
      setMode("login");
    } catch (err: any) {
      setError(getLocalizedError(err, "auth.resetFailed"));
    } finally {
      setLoading(false);
    }
  };

  const completeSocialLogin = useCallback(
    async (
      payload:
        | { provider: "google"; idToken: string }
        | { provider: "linkedin"; accessToken: string }
        | { provider: "apple"; idToken: string }
    ) => {
      setError(null);
      setLoading(true);
      try {
        const result = await socialLoginApi(payload);
        applyAuthSession(result);
        try {
          await syncExplorerOnOtpVerify();
        } catch (syncErr) {
          if (handleExplorerSyncFailure(syncErr)) return;
          throw syncErr;
        }
        navigate(await resolvePostAuthPath(result.userObjectId), { replace: true });
      } catch (err: any) {
        setError(getLocalizedError(err, "auth.socialLoginFailed"));
      } finally {
        setLoading(false);
      }
    },
    [
      navigate,
      applyAuthSession,
      resolvePostAuthPath,
      syncExplorerOnOtpVerify,
      handleExplorerSyncFailure,
      getLocalizedError,
    ]
  );

  useEffect(() => {
    if (socialCallbackHandledRef.current) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const provider = (params.get("provider") || params.get("social_provider") || "").trim().toLowerCase();
    const idToken = (params.get("id_token") || params.get("idToken") || "").trim();
    const accessToken = (params.get("access_token") || params.get("accessToken") || "").trim();
    const providerError = params.get("error_description") || params.get("error");

    if (providerError) {
      socialCallbackHandledRef.current = true;
      setError(t("auth.socialLoginFailed"));
      return;
    }

    if (!provider) {
      return;
    }

    socialCallbackHandledRef.current = true;
    window.history.replaceState({}, document.title, window.location.pathname);

    if (provider === "google" && idToken) {
      void completeSocialLogin({ provider: "google", idToken });
      return;
    }

    if (provider === "linkedin" && accessToken) {
      void completeSocialLogin({ provider: "linkedin", accessToken });
      return;
    }

    if (provider === "apple" && idToken) {
      void completeSocialLogin({ provider: "apple", idToken });
      return;
    }

    setError(t("auth.socialTokenMissing"));
  }, [completeSocialLogin, t]);

  const registerEmailNext = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!canRegisterEmailNext) {
      setError(t("auth.enterValidEmail"));
      return;
    }
    if (needsEmailVerification) {
      setRegisterPhase("verify");
      setVerificationCode("");
      setVerifyStepFromDuplicateEmail(true);
      setVerifyStepFromLogin(false);
      setInfo(t("auth.emailAlreadyExistsVerify"));
      return;
    }
    if (emailExists === true) {
      return;
    }
    if (emailExistsChecking) {
      return;
    }
    if (emailExists === false) {
      setRegisterPhase("profile");
      return;
    }
    setLoading(true);
    try {
      const result = await checkEmailNow();
      if (result.needsEmailVerification) {
        setRegisterPhase("verify");
        setVerificationCode("");
        setVerifyStepFromDuplicateEmail(true);
        setVerifyStepFromLogin(false);
        setInfo(t("auth.emailAlreadyExistsVerify"));
        return;
      }
      if (result.exists) {
        return;
      }
      if (result.exists === false) {
        setRegisterPhase("profile");
      }
    } finally {
      setLoading(false);
    }
  };

  const registerEmailBlocked = emailExists === true && !needsEmailVerification;

  return (
    <div className="auth-flow-root position-relative">
      <div
        className="position-absolute"
        style={{ top: 16, right: 16, zIndex: 3 }}
      >
        <LiveTranslationDropdown variant="on-dark" />
      </div>
      <div className="auth-flow-backdrop" aria-hidden />
      <div className="auth-flow-card">
        <div className="text-center mb-2">
          <div className="auth-flow-brand-wrap mb-0">
            <img src="/connect-logo.png" alt="SMBF logo" className="auth-flow-brand-logo" />
          </div>
          {isExplorerAuthUi ? (
            <>
              {!(mode === "register" && registerPhase === "verify") ? (
                <p className="auth-flow-subtitle small mb-0 px-md-2">
                  {t("auth.explorerPasswordPrompt")}
                </p>
              ) : null}
            </>
          ) : null}
        </div>

        {error ? <div className="alert alert-danger p-3 small mb-3">{error}</div> : null}
        {info ? (
          <div className="alert alert-success p-3 small mb-3 text-center">{info}</div>
        ) : null}

        {showForgotPassword ? (
          <form onSubmit={resetCodeSent ? onResetPassword : onForgotPassword}>
            <h1 className="auth-flow-title h5 mb-2">{t("auth.resetPassword")}</h1>
            <p className="auth-flow-subtitle mb-3">{t("auth.emailCodeNotice")}</p>
            <div className="mb-3">
              <label className="form-label small fw-semibold text-secondary">{t("auth.email")}</label>
              <input
                key={`auth-email-forgot-${uiLang}`}
                className="form-control"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.emailPlaceholder")}
                autoComplete="email"
              />
            </div>

            {resetCodeSent ? (
              <>
                <div className="mb-3">
                  <label className="form-label small fw-semibold text-secondary">{t("auth.resetCode")}</label>
                  <input
                    className="form-control"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    placeholder={t("auth.resetCodePlaceholder")}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-semibold text-secondary">New password</label>
                  <div className="input-group">
                    <input
                      className="form-control"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="e.g. SecurePass1!"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowNewPassword((s) => !s)}
                      aria-label={showNewPassword ? "Hide password" : "Show password"}
                    >
                      {showNewPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                  <PasswordRequirementFeedback feedback={resetPasswordFeedback} />
                </div>
                <button className="btn btn-primary w-100 auth-flow-primary-btn" disabled={loading || !resetCode || !isStrongPassword(newPassword.trim())} type="submit">
                  {loading ? t("auth.pleaseWait") : t("auth.resetPassword")}
                </button>
              </>
            ) : (
              <button className="btn btn-primary w-100 auth-flow-primary-btn" disabled={loading || !email.trim()} type="submit">
                {loading ? t("auth.pleaseWait") : t("auth.sendResetCode")}
              </button>
            )}

            <button
              type="button"
              className="btn btn-link w-100 mt-2 auth-flow-link"
              onClick={() => {
                setShowForgotPassword(false);
                setResetCodeSent(false);
                setResetCode("");
                setNewPassword("");
                setShowNewPassword(false);
                setInfo(null);
                setError(null);
              }}
            >
              {t("auth.backToLogin")}
            </button>
          </form>
        ) : mode === "register" && registerPhase === "verify" ? (
          <form onSubmit={onVerifyEmail}>
            <h1 className="auth-flow-title h5 mb-2 text-center">{t("auth.verificationCodeSentTitle")}</h1>
            <p className="auth-flow-subtitle mb-4 text-center">
              {t("auth.verifyEmailInstructionStart")} <strong>{email.trim()}</strong>
            </p>

            <label htmlFor="otp-digit-0" className="visually-hidden">
              {t("auth.verificationCodeLabel")}
            </label>
            <OtpInput value={verificationCode} onChange={setVerificationCode} disabled={loading} autoFocus />

            <div className="d-grid gap-2 mt-3">
              <button
                type="button"
                className="btn w-100 auth-flow-outline-pill-btn"
                disabled={loading || !isValidEmail(email)}
                onClick={() => void onResendVerificationEmail()}
              >
                {t("auth.resendVerificationCode")}
              </button>

              {verifyStepFromDuplicateEmail || verifyStepFromLogin ? (
                <button
                  type="button"
                  className="btn w-100 auth-flow-outline-pill-btn"
                  disabled={loading}
                  onClick={() => {
                    setVerifyStepFromDuplicateEmail(false);
                    setVerifyStepFromLogin(false);
                    setInfo(null);
                    switchToLogin();
                  }}
                >
                  {t("auth.logIn")}
                </button>
              ) : null}

              <button
                className="btn btn-primary w-100 auth-flow-pill-btn"
                disabled={loading || verificationCode.replace(/\D/g, "").length !== 6}
                type="submit"
              >
                {loading ? t("auth.verifying") : t("auth.verify")}
              </button>

              <button
                type="button"
                className="btn w-100 auth-flow-outline-pill-btn"
                disabled={loading}
                onClick={() => {
                  setVerificationCode("");
                  setError(null);
                  setInfo(null);
                  setVerifyStepFromDuplicateEmail(false);
                  if (verifyStepFromLogin) {
                    setVerifyStepFromLogin(false);
                    switchToLogin();
                  } else {
                    setRegisterPhase("profile");
                  }
                }}
              >
                {t("auth.back")}
              </button>
            </div>
          </form>
        ) : mode === "register" && registerPhase === "profile" ? (
          <form onSubmit={onRegisterFromProfile}>
            <h1 className="auth-flow-title h5 mb-1">{t("auth.addAccountDetails")}</h1>
            <p className="auth-flow-subtitle mb-4">
              {t("auth.signingUpAs")} <strong>{email.trim()}</strong>
            </p>

            <div className="row g-2">
              <div className="col-md-6">
                <label className="form-label small fw-semibold text-secondary">{t("auth.firstName")}</label>
                <input
                  className="form-control"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t("auth.firstName")}
                  autoComplete="given-name"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold text-secondary">{t("auth.lastName")}</label>
                <input
                  className="form-control"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t("auth.lastName")}
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className="mt-3">
              <label className="form-label small fw-semibold text-secondary">{t("auth.password")}</label>
              <div className="input-group">
                <input
                  className="form-control"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("auth.passwordPlaceholder")}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? t("auth.ariaHidePassword") : t("auth.ariaShowPassword")}
                >
                  {showPassword ? t("auth.hide") : t("auth.show")}
                </button>
              </div>
            </div>

            <PasswordRequirementFeedback feedback={registerPasswordFeedback} />
            <button
              className="btn btn-primary w-100 auth-flow-primary-btn mt-4"
              disabled={!canRegisterProfileSubmit || loading}
              type="submit"
            >
              {loading ? t("auth.pleaseWait") : t("auth.continue")}
            </button>

            <button type="button" className="btn btn-outline-secondary w-100 mt-2" disabled={loading} onClick={() => setRegisterPhase("email")}>
              {t("auth.changeEmail")}
            </button>
          </form>
        ) : mode === "register" && registerPhase === "email" ? (
          <form onSubmit={registerEmailNext}>
            <h1 className="auth-flow-title h5 mb-2 text-center">{t("auth.getStarted")}</h1>
            <p className="auth-flow-subtitle mb-4 text-center">{t("auth.createAccountPrompt")}</p>

            <label className="form-label small fw-semibold text-secondary">{t("auth.workEmail")}</label>
            <div className="auth-flow-email-row mb-1">
              <input
                key={`auth-email-register-${uiLang}`}
                className={`form-control${registerEmailBlocked ? " is-invalid" : ""}`}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.emailPlaceholder")}
                autoComplete="email"
                aria-invalid={registerEmailBlocked}
              />
              <button
                className="btn btn-primary auth-flow-primary-btn"
                type="submit"
                disabled={loading || !canRegisterEmailNext || registerEmailBlocked || emailExistsChecking}
              >
                {loading || emailExistsChecking ? t("auth.pleaseWait") : t("auth.signUp")}
              </button>
            </div>
            {emailExistsChecking ? (
              <p className="small text-muted mb-2">{t("auth.checkingEmail")}</p>
            ) : null}
            {needsEmailVerification ? (
              <p className="small text-muted mb-2">{t("auth.emailAlreadyExistsVerify")}</p>
            ) : null}
            {registerEmailBlocked ? (
              <div className="invalid-feedback d-block small mb-2">
                {t("auth.emailAlreadyExistsLogin")}{" "}
                <button type="button" className="auth-flow-link p-0 border-0 bg-transparent align-baseline" onClick={switchToLogin}>
                  {t("auth.logIn")}
                </button>
              </div>
            ) : null}
            <p className="auth-flow-subtitle small mb-0">{t("auth.realEmailHelp")}</p>

            {/*
            <div className="auth-flow-divider">Or continue with</div>

            <SocialLoginButtons
              disabled={loading}
              onGoogleIdToken={(idToken) => void completeSocialLogin({ provider: "google", idToken })}
            />
            */}

            <p className="text-center auth-flow-subtitle mt-4 mb-0">
              {t("auth.alreadyHaveAccount")}{" "}
              <button type="button" className="auth-flow-link" onClick={switchToLogin}>
                {t("auth.logIn")}
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={onLoginSubmit}>
            <h1 className="auth-flow-title h5 mb-2 text-center">{t("auth.welcomeBack")}</h1>
            <p className="auth-flow-subtitle mb-4 text-center">{t("auth.signInPrompt")}</p>

            <div className="mb-3">
              <label className="form-label small fw-semibold text-secondary">{t("auth.email")}</label>
              <input
                key={`auth-email-login-${uiLang}`}
                className="form-control"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.emailPlaceholder")}
                autoComplete="email"
              />
            </div>
            <div className="mb-2">
              <label className="form-label small fw-semibold text-secondary">{t("auth.password")}</label>
              <input
                className="form-control"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("auth.passwordPlaceholder")}
                autoComplete="current-password"
              />
            </div>

            <div className="d-flex justify-content-end mb-3">
              <button
                type="button"
                className="auth-flow-link small"
                onClick={() => {
                  setShowForgotPassword(true);
                  setInfo(null);
                  setError(null);
                }}
                disabled={loading}
              >
                {t("auth.forgotPassword")}
              </button>
            </div>

            <button className="btn btn-primary w-100 auth-flow-primary-btn" disabled={!canLoginSubmit || loading} type="submit">
              {loading ? t("auth.pleaseWait") : t("auth.logIn")}
            </button>

            <div className="auth-flow-divider">{t("auth.orContinueWith")}</div>

            {/* <SocialLoginButtons
              disabled={loading}
              onGoogleIdToken={(idToken) => void completeSocialLogin({ provider: "google", idToken })}
            /> */}

            <p className="text-center auth-flow-subtitle mt-4 mb-0">
              {t("auth.newHere")}{" "}
              <button type="button" className="auth-flow-link" onClick={switchToRegister}>
                {t("auth.createAccount")}
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;
